import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtINR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, Package, Truck, Home } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const STATUS_FLOW = ["confirmed", "packed", "shipped", "delivered"];
const STATUS_LABEL = {
  pending_otp: "Awaiting OTP",
  confirmed: "Confirmed",
  packed: "Packed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export function Orders() {
  useSEO({ title: "My Orders | Joy Pearl", description: "View your Joy Pearl order history, track shipments and download invoices.", noIndex: true });
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  useEffect(() => { if (user) api.get("/orders").then((r) => setOrders(r.data)); }, [user]);

  if (loading) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;
  if (!user) return <div className="jp-container py-20 text-center"><Link to="/login" className="jp-btn-primary">Sign in to view orders</Link></div>;

  return (
    <div className="jp-container py-8 md:py-12" data-testid="orders-page">
      <h1 className="font-serif text-3xl md:text-5xl mb-8">My Orders</h1>
      {orders.length === 0 ? (
        <div className="text-center py-16 text-brand-muted">No orders yet. <Link to="/products" className="text-brand-primary underline">Start shopping</Link></div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Link key={o.id} to={`/orders/${o.id}`} className="block bg-white border border-brand-line p-5 hover:border-brand-primary transition-colors" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-medium">{o.order_number}</p>
                  <p className="text-xs text-brand-muted mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold">{fmtINR(o.total)}</p>
                  <p className="jp-badge mt-1">{STATUS_LABEL[o.status]}</p>
                </div>
              </div>
              <div className="mt-4 flex gap-3 overflow-x-auto">
                {o.items.slice(0, 5).map((it) => (
                  <div key={it.product_id} className="h-14 w-14 bg-brand-blush shrink-0 overflow-hidden">
                    {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover"/>}
                  </div>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(undefined)); }, [id]);

  useSEO({
    title: order ? `Order ${order.order_number} | Joy Pearl` : "Order Confirmed | Joy Pearl",
    description: "Your Joy Pearl order is confirmed. Track shipment and download tax invoice.",
    noIndex: true,
  });

  if (order === undefined) return <div className="jp-container py-20 text-center text-brand-muted">Order not found.</div>;
  if (!order) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;

  const stepIcons = { confirmed: CheckCircle2, packed: Package, shipped: Truck, delivered: Home };
  const currentStep = STATUS_FLOW.indexOf(order.status);

  return (
    <div className="jp-container py-8 md:py-12" data-testid="order-detail-page">
      <Link to="/orders" className="text-sm text-brand-muted hover:text-brand-primary">← Back to orders</Link>
      <h1 className="font-serif text-3xl md:text-4xl mt-3" data-testid="order-number">{order.order_number}</h1>
      <p className="text-sm text-brand-muted mt-1">Placed {new Date(order.created_at).toLocaleString("en-IN")}</p>

      <div className="bg-white border border-brand-line p-6 mt-6">
        <h2 className="font-serif text-lg mb-6">Order Status: <span className="text-brand-primary">{STATUS_LABEL[order.status]}</span></h2>
        {order.status !== "cancelled" && order.status !== "pending_otp" && (
          <div className="grid grid-cols-4 gap-2">
            {STATUS_FLOW.map((s, i) => {
              const Icon = stepIcons[s];
              const done = i <= currentStep;
              return (
                <div key={s} className="flex flex-col items-center text-center">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${done ? "bg-brand-primary text-white" : "bg-brand-line text-brand-muted"}`}><Icon size={18}/></div>
                  <p className={`text-xs mt-2 uppercase tracking-wider ${done ? "text-brand-ink" : "text-brand-muted"}`}>{STATUS_LABEL[s]}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[1fr_360px] gap-8 mt-8">
        <div className="bg-white border border-brand-line p-6">
          <h3 className="font-serif text-lg mb-4">Items</h3>
          <div className="space-y-4">
            {order.items.map((it) => (
              <div key={it.product_id} className="flex gap-4 border-b border-brand-line pb-4 last:border-0">
                <div className="h-16 w-16 bg-brand-blush overflow-hidden">{it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover"/>}</div>
                <div className="flex-1">
                  <p className="font-medium">{it.name}</p>
                  <p className="text-sm text-brand-muted mt-1">Qty {it.qty} • {fmtINR(it.price)}</p>
                </div>
                <p className="font-medium">{fmtINR(it.price * it.qty)}</p>
              </div>
            ))}
          </div>
        </div>

        <aside className="space-y-6">
          <div className="bg-white border border-brand-line p-6">
            <h3 className="font-serif text-lg mb-4">Print</h3>
            <a href={`/print/${order.id}?mode=invoice`} target="_blank" rel="noopener noreferrer" className="jp-btn-outline w-full justify-center mb-2" data-testid="print-invoice-link">Tax Invoice</a>
            <a href={`/print/${order.id}?mode=packing`} target="_blank" rel="noopener noreferrer" className="jp-btn-outline w-full justify-center" data-testid="print-packing-link">Packing Slip</a>
          </div>
          <div className="bg-white border border-brand-line p-6">
            <h3 className="font-serif text-lg mb-4">Delivery</h3>
            <p className="text-sm font-medium">{order.address.name} • {order.address.phone}</p>
            <p className="text-sm text-brand-muted mt-1 leading-relaxed">
              {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br/>
              {order.address.city}, {order.address.state} {order.address.pincode}
            </p>
          </div>
          <div className="bg-white border border-brand-line p-6">
            <h3 className="font-serif text-lg mb-4">Payment</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-brand-muted">Subtotal</span><span>{fmtINR(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-brand-muted">Shipping</span><span>{order.shipping === 0 ? "Free" : fmtINR(order.shipping)}</span></div>
              <div className="border-t border-brand-line my-3"/>
              <div className="flex justify-between font-semibold text-base"><span>Total</span><span>{fmtINR(order.total)}</span></div>
              <p className="text-xs text-brand-muted mt-2">Method: COD</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
